'use client'

import React, { useCallback } from 'react'
import { ChevronDown } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

const STORAGE_KEY = 'martini-inspector-collapsed-sections'
const EXPANDED_STORAGE_KEY = 'martini-inspector-expanded-sections'

function readSet(storageKey: string): Set<string> {
  if (typeof window === 'undefined') return new Set()
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return new Set()
    return new Set(JSON.parse(raw) as string[])
  } catch {
    return new Set()
  }
}

function writeSet(storageKey: string, keys: Set<string>): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify([...keys]))
  } catch {
    /* noop */
  }
}

/** Toggle a section key in the persisted collapsed set. Returns the new value. */
export function toggleSectionCollapsed(key: string, defaultCollapsed = false): boolean {
  const collapsed = readSet(STORAGE_KEY)
  const expanded = readSet(EXPANDED_STORAGE_KEY)
  const wasCollapsed = isSectionCollapsed(key, defaultCollapsed)
  if (wasCollapsed) {
    collapsed.delete(key)
    expanded.add(key)
  } else {
    collapsed.add(key)
    expanded.delete(key)
  }
  writeSet(STORAGE_KEY, collapsed)
  writeSet(EXPANDED_STORAGE_KEY, expanded)
  return !wasCollapsed
}

/** Check if a section key is collapsed. */
export function isSectionCollapsed(key: string, defaultCollapsed = false): boolean {
  const collapsed = readSet(STORAGE_KEY)
  if (collapsed.has(key)) return true
  const expanded = readSet(EXPANDED_STORAGE_KEY)
  if (expanded.has(key)) return false
  return defaultCollapsed
}

// ---------------------------------------------------------------------------
// React hook — thin wrapper that triggers re-render on toggle.
// ---------------------------------------------------------------------------

export function useSectionCollapsed(key: string, defaultCollapsed = false): [boolean, () => void] {
  const [preference, setPreference] = React.useState(() => ({
    key,
    defaultCollapsed,
    collapsed: isSectionCollapsed(key, defaultCollapsed),
  }))
  if (preference.key !== key || preference.defaultCollapsed !== defaultCollapsed) {
    setPreference({ key, defaultCollapsed, collapsed: isSectionCollapsed(key, defaultCollapsed) })
  }
  const toggle = useCallback(() => {
    setPreference({ key, defaultCollapsed, collapsed: toggleSectionCollapsed(key, defaultCollapsed) })
  }, [key, defaultCollapsed])
  return [preference.collapsed, toggle]
}

// ---------------------------------------------------------------------------
// InspectorSection — collapsible section with label + chevron.
// ---------------------------------------------------------------------------

interface InspectorSectionProps {
  /** Unique key for persisting collapsed state (e.g. "video-prompt"). */
  sectionKey: string
  label: string
  children: React.ReactNode
  /** Extra className on the outer wrapper. */
  className?: string
  /** Inline content rendered right after the label text (e.g. help tooltip).
   *  Only visible when the section is expanded. Lives inside the same flex
   *  group as the label so it visually anchors to the concept the label
   *  names. Clicks on this slot don't propagate to the row toggle. */
  labelHelp?: React.ReactNode
  /** Optional content rendered to the right of the label (e.g. copy button).
   *  Only visible when the section is expanded. Clicks on this slot don't
   *  propagate to the row toggle. */
  headerRight?: React.ReactNode
  /** Render children regardless of the persisted collapsed preference, and
   *  no-op the toggle while this flag is true so the user can't silently
   *  flip the saved choice (a click would otherwise update localStorage
   *  without any visible feedback and snap the section to the wrong state
   *  once the flag clears). Once the flag drops, the section reverts to the
   *  saved preference. Used to surface in-flight progress UI (e.g. an
   *  active upscale) that the user shouldn't have to manually expand. */
  forceExpanded?: boolean
  /** Initial preference, used until the user expands or collapses this section. */
  defaultCollapsed?: boolean
}

export function InspectorSection({
  sectionKey,
  label,
  children,
  className,
  labelHelp,
  headerRight,
  forceExpanded,
  defaultCollapsed = false,
}: InspectorSectionProps) {
  const [collapsed, toggle] = useSectionCollapsed(sectionKey, defaultCollapsed)
  const expanded = forceExpanded || !collapsed
  // While `forceExpanded` is true the section ignores `collapsed`, so a
  // click on the header would silently flip the persisted preference
  // without any visible feedback — and once the flag clears, the section
  // would snap to the wrong state. No-op the toggle in that window.
  const isToggleable = !forceExpanded

  const handleRowClick = isToggleable ? () => toggle() : undefined
  const handleRowKeyDown = isToggleable
    ? (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          toggle()
        }
      }
    : undefined

  return (
    <div className={className}>
      <div
        data-quiet-focus
        role={isToggleable ? 'button' : undefined}
        tabIndex={isToggleable ? 0 : undefined}
        aria-expanded={isToggleable ? expanded : undefined}
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
        className={cn(
          'group flex items-center justify-between mb-2 gap-2 select-none rounded focus-visible:bg-secondary/50',
          isToggleable && 'cursor-pointer',
        )}
      >
        <div className="flex items-center min-w-0 flex-1 gap-1.5">
          <span className="text-sm font-semibold text-foreground group-hover:text-foreground/80 transition-colors truncate">
            {label}
          </span>
          {expanded && labelHelp && (
            // Stops clicks on the help affordance from bubbling up and
            // toggling the section — the row's `onClick` would otherwise
            // run after the help button's own handler.
            <div onClick={stopRowToggle} onKeyDown={stopRowToggle}>
              {labelHelp}
            </div>
          )}
        </div>
        {expanded && headerRight && (
          <div onClick={stopRowToggle} onKeyDown={stopRowToggle}>
            {headerRight}
          </div>
        )}
        <ChevronDown
          className={cn(
            'w-3 h-3 text-muted-foreground/40 group-hover:text-muted-foreground/60 transition-transform duration-150 shrink-0',
            !expanded && '-rotate-90',
          )}
          strokeWidth={1.5}
          aria-hidden
        />
      </div>
      {expanded && children}
    </div>
  )
}

/** Prevents clicks on inner interactive content (help buttons, copy buttons,
 *  etc.) from bubbling up and toggling the section. */
function stopRowToggle(event: React.SyntheticEvent) {
  event.stopPropagation()
}
